import React, { FC } from 'react';
import { useDropzone } from 'react-dropzone';
import TooltipWrapper from 'components/TooltipWrapper';
import styled from 'styled-components';
import { FaTrash } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { formatBytes } from 'utils/helpers/formatters';
import Button from '../Button';

interface IInnerDropzoneProps {
  allFiles: File[];
  removeFile: (file: File) => any;
  fileSizes?: Record<string, number>;
}

const InnerDropzone: FC<IInnerDropzoneProps> = ({ allFiles, removeFile, fileSizes }) => {
  const { getRootProps } = useDropzone({ noDragEventsBubbling: true });
  const { t } = useTranslation();
  return (
    <Files>
      {allFiles!.map((file: File) => {
        const displaySize = file.size || fileSizes?.[file.name] || 0;

        return (
          <SingleFile
            {...getRootProps({ onClick: e => e.stopPropagation() })}
            key={file.name}
          >
            <FileBox>
              <div className="font-weight-bold">{formatBytes(displaySize)}</div>
              <TooltipWrapper
                id={`tooltip-file-${file.name}`}
                overlay={file.name}
              >
                {file.name}
              </TooltipWrapper>
            </FileBox>
            <div className="mt-2">
              <TooltipWrapper
                id="tooltip-trash"
                overlay={t('common.delete')}
              >
                <Button
                  btnType="danger"
                  size="sm"
                  outlined
                  onClick={removeFile(file)}
                  preventDebounce
                >
                  <FaTrash size={14} />
                </Button>
              </TooltipWrapper>
            </div>
          </SingleFile>
        );
      })}
    </Files>
  );
};

const Files = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;

  &:focus {
    outline: none;
  }
`;

const SingleFile = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 1rem 0.75rem;
  color: ${props => props.theme?.colors?.dark || '#212529'};
`;

const FileBox = styled.div`
  border-radius: ${props => props.theme?.borderRadius || '0.25rem'};
  width: 100px;
  height: 100px;
  overflow: hidden;
  background: ${props => props.theme?.colors?.gray || '#e9ecef'};
  font-size: 12px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;
  box-shadow: ${props => props.theme?.boxShadow || 'none'};

  span {
    background: ${props => props.theme?.colors?.light || '#f8f9fa'};
    padding: 0.125rem;
    border-radius: ${props => props.theme?.borderRadius || '0.25rem'};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block !important;
  }
`;

export default InnerDropzone;
